import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@bikes/routes/Routes';
import { useBikesStore } from '@bikes/store/store';
import { MultipleOptionsTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { parseZodErrors, scrollTop, stringFieldSchema } from '@utils';
import { DEFECT_OPTIONS_ERROR } from '@bikes/constants/errorMessages';
import { ZodError } from 'zod';
import { HireBikeCompanies, displayText } from '@bikes/constants/hireCompanies';

export const SelectHireCompany: FunctionComponent = () => {
  const navigate = useNavigate();

  const { hireCompaniesOptions, updateHireCompany, bikesType, hireCompany } =
    useBikesStore();
  const [selectedType, setSelectedType] = useState<HireBikeCompanies | null>(
    hireCompany,
  );
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);

  // redirect to the landing page if the bikesType or companies are not set
  useEffect(() => {
    if (!bikesType || !hireCompaniesOptions) {
      navigate(RoutesList.TEST_LANDING_PAGE);
    }
    // @ts-ignore - react-hooks/exhaustive-deps
  }, []);

  if (hireCompaniesOptions === null) {
    return null;
  }

  const context = bikesType === 'hire_scooters' ? 'scooter' : 'bike';
  const title = `What company is the hire ${context} from?`;

  const options = hireCompaniesOptions.map((type) => ({
    id: type.title,
    name: type.title,
    labelText: displayText(type.title) || '',
    description: type.description || '',
    radio: type.title,
    imageSrc: type.image,
    isDefault: type.title === hireCompany,
  }));

  const buttonPrevious = {
    label: 'Previous',
    onClick: () => {
      navigate(RoutesList.BIKES_LOCATION);
      scrollTop();
    },
  };

  const buttonNext = {
    label: 'Next',
    onClick: () => {
      try {
        const validatedData = stringFieldSchema(
          'hireCompany',
          DEFECT_OPTIONS_ERROR.required.message,
        ).parse({
          hireCompany: selectedType || '',
        });

        updateHireCompany(validatedData.hireCompany as HireBikeCompanies);

        navigate(RoutesList.WHAT_IS_ISSUE);
        scrollTop();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            href: error.path,
          })),
        );
      }
    },
  };

  const onChange = (result: string) => {
    const updatedResult = options.filter(
      (option) => option.labelText === result,
    )[0].id;
    setSelectedType(updatedResult as HireBikeCompanies);
    setErrorMessages([]);
  };

  return (
    <MultipleOptionsTemplate
      title={title}
      options={options}
      buttonPrevious={buttonPrevious}
      buttonNext={buttonNext}
      onChange={onChange}
      errorPath="hireCompany"
      errorMessages={errorMessages}
    />
  );
};
